import React from 'react';
import {Typography, Box, Grid} from '@mui/material';
import {toPairs, groupBy} from 'lodash';
import {testAccountDetails} from '../../../tests/e2e/e2eCuroTestAccounts';
import {TestAccountActions} from './TestAccountActions';

interface TestAccountsListProps {
  title: string;
  accountsData: Record<string, any>;
  onCloseAllModals?: () => void;
}

export const TestAccountsList: React.FC<TestAccountsListProps> = ({
  title,
  accountsData,
  onCloseAllModals,
}) => {
  // Group accounts by type and sort them
  const sortAccountsByType = (accounts: [string, testAccountDetails][]) => {
    return accounts.sort(([nameA, accountA], [nameB, accountB]) => {
      const typeOrder: Record<string, number> = {
        sfp: 0,
        'spp da': 1,
        'spp ar': 2,
      };
      // First sort by type
      const typeComparison = typeOrder[accountA.type] - typeOrder[accountB.type];
      if (typeComparison !== 0) {
        return typeComparison;
      }

      // Extract numbers from the names for sorting
      const extractNumber = (name: string) => {
        const match = name.match(/(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      };

      const numberA = extractNumber(nameA);
      const numberB = extractNumber(nameB);

      // Sort by number, then by name if numbers are equal
      if (numberA !== numberB) {
        return numberA - numberB;
      }

      return nameA.localeCompare(nameB);
    });
  };

  const groupAccountsByType = (accounts: [string, testAccountDetails][]) => {
    return groupBy(accounts, ([_name, account]) => account.type);
  };

  const renderAccountGroup = (
    type: string,
    accountList: [string, testAccountDetails][]
  ) => {
    const typeDisplayNames: Record<string, string> = {
      sfp: 'SFP',
      'spp da': 'SPP DA',
      'spp ar': 'SPP AR',
    };

    return (
      <Box key={type} mb={2}>
        <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
          {typeDisplayNames[type]}
        </Typography>
        <Grid container spacing={1}>
          {sortAccountsByType(accountList).map(([name, account]) => (
            <Grid item xs={12} sm={6} md={4} key={account.curoId}>
              <TestAccountActions
                name={name}
                account={account}
                onCloseAllModals={onCloseAllModals}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const accountPairs = toPairs(accountsData) as [string, testAccountDetails][];
  const groupedAccounts = groupAccountsByType(accountPairs);

  return (
    <Box mb={3}>
      <Typography variant="h6" sx={{mb: 2, fontWeight: 'bold'}}>
        {title}
      </Typography>
      {Object.entries(groupedAccounts).map(([type, accountList]) =>
        renderAccountGroup(type, accountList)
      )}
    </Box>
  );
};
